import React from "react";
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import Box from '@material-ui/core/Box';

/**
 * Loss per kilo for ONE bulk (same formula as the grid and the Excel):
 *   (Orig bulk Fac wt - Orig Field wt sum) / Orig Field wt sum
 * Returns a number or null when it cannot be calculated.
 */
function calcDifferencePerKilo(facWt, fieldWtSum) {
    const fac = facWt != null ? parseFloat(facWt) : NaN;
    const field = fieldWtSum != null ? parseFloat(fieldWtSum) : NaN;
    if (isNaN(fac) || isNaN(field) || field <= 0) return null;
    return (fac - field) / field;
}

const bulkKeyOf = (row) =>
    row.bulkNo != null ? row.bulkNo : `__nobulk__GTN__${row.gtnNumber}`;

/**
 * Same logic as the grid / Excel sheet:
 *   differencePerKilo = (bulk factory wt - bulk field wt sum) / bulk field wt sum   (L5)
 *   origFacWt (bag)   = field bag wt + differencePerKilo * field bag wt            (R column)
 */
function applyBulkWeights(rows) {
    const fieldWtSums = {};
    rows.forEach((row) => {
        const key = bulkKeyOf(row);
        fieldWtSums[key] = (fieldWtSums[key] || 0) + parseFloat(row.origFieldWt || 0);
    });

    return rows.map((row) => {
        const diff = calcDifferencePerKilo(row.origBulkFacWt, fieldWtSums[bulkKeyOf(row)]);
        const next = { ...row, differencePerKilo: diff };

        if (diff != null) {
            const fieldWt = row.fieldBagNo != null ? parseFloat(row.origFieldWt || 0) : null;
            const bagFacWt = fieldWt != null ? fieldWt + (diff * fieldWt) : null;
            next.origFacWt = bagFacWt;
            next.displayOrigFacWt = bagFacWt;
        }
        return next;
    });
}

function splitCommaSeparatedValue(value) {
    if (value == null || value === "") return [];
    return String(value)
        .split(',')
        .map(item => item.trim())
        .filter(item => item !== "");
}

function getSplitValue(value, index, totalCount) {
    const values = splitCommaSeparatedValue(value);
    if (values.length === totalCount) return values[index];
    return index === 0 ? value : null;
}

function getDisplayOrigFacWt(row) {
    const value = row.displayOrigFacWt != null ? row.displayOrigFacWt : row.origFacWt;
    return value != null && !isNaN(parseFloat(value)) ? parseFloat(value).toFixed(2) : "-";
}

function expandFactoryTagRows(rows) {
    if (!Array.isArray(rows)) return [];

    const expandedRows = [];
    const bulkGroupsMap = new Map();

    rows.forEach((row) => {
        const bKey = row.bulkNo != null ? row.bulkNo : `__nobulk__GTN__${row.gtnNumber}`;
        if (!bulkGroupsMap.has(bKey)) bulkGroupsMap.set(bKey, []);
        bulkGroupsMap.get(bKey).push(row);
    });

    bulkGroupsMap.forEach((rowsInBulk) => {
        const factorySourceRow = rowsInBulk.find(row => splitCommaSeparatedValue(row.facBagNo).length > 1);

        if (!factorySourceRow) {
            rowsInBulk.forEach(row => expandedRows.push(row));
            return;
        }

        const factoryTags = splitCommaSeparatedValue(factorySourceRow.facBagNo);
        const rowCount = Math.max(rowsInBulk.length, factoryTags.length);

        for (let index = 0; index < rowCount; index++) {
            const sourceRow = rowsInBulk[index] || factorySourceRow;
            const origFacWt = getSplitValue(factorySourceRow.origFacWt, index, factoryTags.length);
            expandedRows.push({
                ...sourceRow,
                fieldBagNo: rowsInBulk[index] ? rowsInBulk[index].fieldBagNo : null,
                origFieldWt: rowsInBulk[index] ? rowsInBulk[index].origFieldWt : null,
                fieldTime: rowsInBulk[index] ? rowsInBulk[index].fieldTime : null,
                facBagNo: factoryTags[index] || null,
                origFacWt: origFacWt,
                displayOrigFacWt: origFacWt != null ? origFacWt : factorySourceRow.origFacWt,
                facTime: getSplitValue(factorySourceRow.facTime, index, factoryTags.length) || factorySourceRow.facTime,
                facWeighingEventCount: Math.max(factorySourceRow.facWeighingEventCount || 0, factoryTags.length)
            });
        }
    });

    return applyBulkWeights(expandedRows);
}

export default class ComponentToPrint extends React.Component {
    render() {
        const searchData = this.props.searchData;
        const reportData = expandFactoryTagRows(this.props.reportData);

        const fieldGroupKey = (row) => `${row.divisionName}|${row.sessionName}|${row.operatorName}|${row.fieldNo}`;

        const effectiveBulkKey = (row) =>
            row.bulkNo != null ? row.bulkNo : `__nobulk__GTN__${row.gtnNumber}`;

        const sortedReportData = [];
        const fieldTripletSpanMap = new Map();

        {
            const bulkGroupsMap = new Map();
            reportData.forEach((row) => {
                const bKey = effectiveBulkKey(row);
                if (!bulkGroupsMap.has(bKey)) bulkGroupsMap.set(bKey, []);
                bulkGroupsMap.get(bKey).push(row);
            });

            bulkGroupsMap.forEach((rowsInBulk) => {
                const fieldSubGroupsMap = new Map();
                rowsInBulk.forEach((row) => {
                    const fKey = fieldGroupKey(row);
                    if (!fieldSubGroupsMap.has(fKey)) fieldSubGroupsMap.set(fKey, []);
                    fieldSubGroupsMap.get(fKey).push(row);
                });

                fieldSubGroupsMap.forEach((rowsInField) => {
                    const fieldRows = rowsInField.filter(r => r.fieldBagNo != null);
                    const factoryOnlyRows = rowsInField.filter(r => r.fieldBagNo == null);
                    const actualCount = fieldRows.length;
                    const extraCount = factoryOnlyRows.length;

                    if (actualCount === 0) {
                        factoryOnlyRows.forEach((row, idx) => {
                            sortedReportData.push(row);
                            if (idx === 0) fieldTripletSpanMap.set(row, factoryOnlyRows.length || 1);
                        });
                        return;
                    }

                    let factoryIdx = 0;
                    fieldRows.forEach((fRow, i) => {
                        const rowRank = i + 1;
                        const baseSpan = Math.floor(extraCount / actualCount);
                        const remainder = extraCount % actualCount;
                        const getsExtraRow = rowRank > (actualCount - remainder);
                        const ownCount = baseSpan + (getsExtraRow ? 1 : 0);
                        const runLength = 1 + ownCount;

                        sortedReportData.push(fRow);
                        fieldTripletSpanMap.set(fRow, runLength);

                        for (let k = 0; k < ownCount && factoryIdx < factoryOnlyRows.length; k++) {
                            sortedReportData.push(factoryOnlyRows[factoryIdx]);
                            factoryIdx++;
                        }
                    });

                    while (factoryIdx < factoryOnlyRows.length) {
                        sortedReportData.push(factoryOnlyRows[factoryIdx]);
                        factoryIdx++;
                    }
                });
            });
        }

        const fieldRunSpanMap = new Map();
        {
            let i = 0;
            while (i < sortedReportData.length) {
                const key = fieldGroupKey(sortedReportData[i]);
                let j = i + 1;
                while (j < sortedReportData.length && fieldGroupKey(sortedReportData[j]) === key) j++;
                fieldRunSpanMap.set(sortedReportData[i], j - i);
                i = j;
            }
        }

        const rowSpanCounts = {};
        const bulkFieldWtSums = {};
        sortedReportData.forEach((row) => {
            const bKey = effectiveBulkKey(row);
            rowSpanCounts[bKey] = (rowSpanCounts[bKey] || 0) + 1;
            bulkFieldWtSums[bKey] =
                (bulkFieldWtSums[bKey] || 0) + parseFloat(row.origFieldWt || 0);
        });

        const bulkOrder = [];
        const bulkSeenSet = new Set();
        const bulkGtn = {};
        const bulkIsReal = {};
        sortedReportData.forEach((row) => {
            const bKey = effectiveBulkKey(row);
            if (!bulkSeenSet.has(bKey)) {
                bulkSeenSet.add(bKey);
                bulkOrder.push(bKey);
                bulkGtn[bKey] = row.gtnNumber;
                bulkIsReal[bKey] = row.bulkNo != null;
            }
        });
        const gtnRunStartOfBulk = {};
        const gtnRunSpan = {};
        const bulkNumberMap = {};
        {
            let bi = 0;
            while (bi < bulkOrder.length) {
                const runStart = bulkOrder[bi];
                const gtnVal = bulkGtn[runStart];
                let total = rowSpanCounts[runStart];
                let bulkCounter = 1;
                if (bulkIsReal[runStart]) bulkNumberMap[runStart] = bulkCounter++;
                let bj = bi + 1;
                while (bj < bulkOrder.length && bulkGtn[bulkOrder[bj]] != null && bulkGtn[bulkOrder[bj]] === gtnVal) {
                    gtnRunStartOfBulk[bulkOrder[bj]] = runStart;
                    if (bulkIsReal[bulkOrder[bj]]) bulkNumberMap[bulkOrder[bj]] = bulkCounter++;
                    total += rowSpanCounts[bulkOrder[bj]];
                    bj++;
                }
                gtnRunStartOfBulk[runStart] = runStart;
                gtnRunSpan[runStart] = total;
                bi = bj;
            }
        }

        const renderedBulks = new Set();
        const shownGtnValues = new Set();

        const totals = reportData.reduce((acc, row) => {
            if (row.fieldBagNo != null) acc.fieldBagCount += 1;
            acc.origFieldWt += parseFloat(row.origFieldWt || 0);
            if (row.facBagNo != null) acc.facBagCount += 1;
            acc.origFacWt += parseFloat(row.origFacWt || 0);
            return acc;
        }, { fieldBagCount: 0, origFieldWt: 0, facBagCount: 0, origFacWt: 0 });

        const facTagCounts = {};
        const fieldTagCounts = {};
        sortedReportData.forEach((row) => {
            if (row.facBagNo) facTagCounts[row.facBagNo] = (facTagCounts[row.facBagNo] || 0) + 1;
            if (row.fieldBagNo) fieldTagCounts[row.fieldBagNo] = (fieldTagCounts[row.fieldBagNo] || 0) + 1;
        });
        const isDupFacTag = (tag) => tag != null && facTagCounts[tag] > 1;
        const isDupFieldTag = (tag) => tag != null && fieldTagCounts[tag] > 1;

        return (
            <div>
                <h3 className="pr-2"><center><u>QR Tag Bag Wise Report</u></center></h3>
                <div className="row pl-2 pb-4 pt-4">
                    <div className="col"><b>Group: </b> {searchData.group}</div>
                    <div className="col"><b>Operation Entity: </b> {searchData.estate}</div>
                    <div className="col"><b>Collection Point: </b> {searchData.division === "0" ? "All Divisions" : searchData.division}</div>
                    <div className="col"><b>Date: </b> {searchData.date}</div>
                </div>
                <div className="table-responsive row-top-gap">
                    <Box display="flex" justifyContent="flex-end" p={2}>
                        <TableContainer component={Paper}>
                            <Table aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell rowSpan={2} align="left" style={{ fontWeight: "bold" }}>Collection Point</TableCell>
                                        <TableCell rowSpan={2} align="left" style={{ fontWeight: "bold" }}>Session</TableCell>
                                        <TableCell rowSpan={2} align="left" style={{ fontWeight: "bold" }}>Operator</TableCell>
                                        <TableCell rowSpan={2} align="left" style={{ fontWeight: "bold" }}>Field No</TableCell>
                                        <TableCell colSpan={3} align="center" style={{ background: "lightgreen", fontWeight: "bold" }}>Field Process</TableCell>
                                        <TableCell colSpan={9} align="center" style={{ background: "#8ca3c7", fontWeight: "bold" }}>Factory Process</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        {/* Field Process sub-columns */}
                                        <TableCell align="left" style={{ fontWeight: "bold" }}>Bag No</TableCell>
                                        <TableCell align="right" style={{ fontWeight: "bold" }}>Orig wt</TableCell>
                                        <TableCell align="left" style={{ fontWeight: "bold" }}>Time</TableCell>

                                        {/* Factory Process sub-columns */}
                                        <TableCell align="left" style={{ fontWeight: "bold" }}>Bulk No</TableCell>
                                        <TableCell align="right" style={{ fontWeight: "bold" }}>Orig bulk Fac wt</TableCell>
                                        <TableCell align="right" style={{ fontWeight: "bold" }}>Orig Field wt</TableCell>
                                        <TableCell align="right" style={{ fontWeight: "bold" }}>Diffrence per Kilo</TableCell>
                                        <TableCell align="left" style={{ fontWeight: "bold" }}>Bag No</TableCell>
                                        <TableCell align="right" style={{ fontWeight: "bold" }}>Orig Fac wt bag wise</TableCell>
                                        <TableCell align="left" style={{ fontWeight: "bold" }}>Time</TableCell>
                                        <TableCell align="left" style={{ fontWeight: "bold" }}>GTN/GRN No</TableCell>
                                        <TableCell align="left" style={{ fontWeight: "bold" }}>Remark</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedReportData.map((row, index) => {
                                        const bulkKey = effectiveBulkKey(row);
                                        const isFirstOfBulk = !renderedBulks.has(bulkKey);
                                        renderedBulks.add(bulkKey);
                                        const span = rowSpanCounts[bulkKey];

                                        const gtnRunStartBulk = gtnRunStartOfBulk[bulkKey];
                                        const isFirstOfGtnRun = isFirstOfBulk && gtnRunStartBulk === bulkKey;
                                        const gtnSpan = gtnRunSpan[gtnRunStartBulk];

                                        const isFirstOfFieldRun = fieldRunSpanMap.has(row);
                                        const fieldSpan = isFirstOfFieldRun ? fieldRunSpanMap.get(row) : 1;

                                        const showFieldTriplet = fieldTripletSpanMap.has(row);
                                        const fieldTripletRowSpan = showFieldTriplet ? fieldTripletSpanMap.get(row) : 1;

                                        let gtnCellText = row.gtnNumber;
                                        if (isFirstOfGtnRun) {
                                            if (row.bulkNo != null) {
                                                if (shownGtnValues.has(row.gtnNumber)) {
                                                    gtnCellText = "";
                                                } else {
                                                    shownGtnValues.add(row.gtnNumber);
                                                }
                                            }
                                        }

                                        // Bulk-wise loss per kilo (same formula as the grid and the Excel)
                                        const bulkDiff = calcDifferencePerKilo(row.origBulkFacWt, bulkFieldWtSums[bulkKey]);

                                        return (
                                            <TableRow key={index}>
                                                {isFirstOfFieldRun && (
                                                    <TableCell rowSpan={fieldSpan} component="th" scope="row">{row.divisionName}</TableCell>
                                                )}
                                                {isFirstOfFieldRun && (
                                                    <TableCell rowSpan={fieldSpan} component="th" scope="row">{row.sessionName}</TableCell>
                                                )}
                                                {isFirstOfFieldRun && (
                                                    <TableCell rowSpan={fieldSpan} component="th" scope="row">{row.operatorName}</TableCell>
                                                )}
                                                {isFirstOfFieldRun && (
                                                    <TableCell rowSpan={fieldSpan} component="th" scope="row">{row.fieldNo}</TableCell>
                                                )}
                                                {showFieldTriplet && (
                                                    <TableCell
                                                        rowSpan={fieldTripletRowSpan}
                                                        component="th"
                                                        scope="row"
                                                        style={isDupFieldTag(row.fieldBagNo) ? { background: "#ffcdd2", fontWeight: "bold" } : undefined}
                                                        title={isDupFieldTag(row.fieldBagNo) ? "Duplicate QR code in this report" : undefined}
                                                    >{row.fieldBagNo}</TableCell>
                                                )}
                                                {showFieldTriplet && (
                                                    <TableCell rowSpan={fieldTripletRowSpan} component="th" align="right" scope="row">{row.origFieldWt}</TableCell>
                                                )}
                                                {showFieldTriplet && (
                                                    <TableCell rowSpan={fieldTripletRowSpan} component="th" scope="row">{row.fieldTime}</TableCell>
                                                )}

                                                {isFirstOfBulk && (
                                                    <TableCell rowSpan={span} component="th" scope="row">
                                                        {bulkNumberMap[bulkKey] != null ? bulkNumberMap[bulkKey] : "-"}
                                                    </TableCell>
                                                )}
                                                {isFirstOfBulk && (
                                                    <TableCell rowSpan={span} component="th" align="right" scope="row">
                                                        {row.origBulkFacWt != null ? parseFloat(row.origBulkFacWt).toFixed(2) : "-"}
                                                    </TableCell>
                                                )}
                                                {isFirstOfBulk && (
                                                    <TableCell rowSpan={span} component="th" align="right" scope="row">
                                                        {parseFloat(bulkFieldWtSums[bulkKey] || 0).toFixed(2)}
                                                    </TableCell>
                                                )}
                                                {isFirstOfBulk && (
                                                    <TableCell rowSpan={span} component="th" align="right" scope="row">
                                                        {bulkDiff != null ? bulkDiff.toFixed(2) : "-"}
                                                    </TableCell>
                                                )}

                                                {row.facWeighingEventCount > 1 ? (
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                        style={isDupFacTag(row.facBagNo) ? { background: "#ffcdd2", fontWeight: "bold" } : undefined}
                                                        title={isDupFacTag(row.facBagNo) ? "Duplicate QR code in this report" : undefined}
                                                    >
                                                        {row.facBagNo}
                                                    </TableCell>
                                                ) : (
                                                    isFirstOfBulk && (
                                                        <TableCell
                                                            rowSpan={span}
                                                            component="th"
                                                            scope="row"
                                                            style={isDupFacTag(row.facBagNo) ? { background: "#ffcdd2", fontWeight: "bold" } : undefined}
                                                            title={isDupFacTag(row.facBagNo) ? "Duplicate QR code in this report" : undefined}
                                                        >
                                                            {row.facBagNo}
                                                        </TableCell>
                                                    )
                                                )}
                                                <TableCell component="th" align="right" scope="row">
                                                    {getDisplayOrigFacWt(row)}
                                                </TableCell>
                                                {row.facWeighingEventCount > 1 ? (
                                                    <TableCell component="th" scope="row">{row.facTime != null ? row.facTime : "-"}</TableCell>
                                                ) : (
                                                    isFirstOfBulk && (
                                                        <TableCell rowSpan={span} component="th" scope="row">{row.facTime != null ? row.facTime : "-"}</TableCell>
                                                    )
                                                )}
                                                {isFirstOfGtnRun && (
                                                    <TableCell rowSpan={gtnSpan} component="th" scope="row">{gtnCellText}</TableCell>
                                                )}
                                                {isFirstOfBulk && (
                                                    <TableCell rowSpan={span} component="th" scope="row">{row.remark}</TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" style={{ fontWeight: "bold" }}>Total</TableCell>
                                        <TableCell align="right" style={{ fontWeight: "bold" }}>{totals.fieldBagCount}</TableCell>
                                        <TableCell align="right" style={{ fontWeight: "bold" }}>{parseFloat(totals.origFieldWt).toFixed(2)}</TableCell>
                                        <TableCell />
                                        <TableCell colSpan={4} />
                                        <TableCell align="right" style={{ fontWeight: "bold" }}>{totals.facBagCount}</TableCell>
                                        <TableCell align="right" style={{ fontWeight: "bold" }}>{parseFloat(totals.origFacWt).toFixed(2)}</TableCell>
                                        <TableCell colSpan={3} />
                                    </TableRow>
                                </TableHead>
                            </Table>
                        </TableContainer>
                    </Box>
                </div>
            </div>
        );
    }
}